import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  InferGetStaticPropsType,
} from 'next';
import React from 'react';

import { GlossaryTermPage } from '../../components/Glossary';
import Layout from '../../components/Layout';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { AugmentedArtistCard } from '../../types/Artists';
import { GlossaryIndex, GlossaryTerm } from '../../types/Glossary';
import {
  fetchByGlossaryTerm,
  fetchGlossary,
  fetchGlossaryIndex,
  fetchGlossaryResults,
  GlossaryLink,
  mapGlossaryLinks,
} from '../../utils/glossary';
import { InsightItem } from '../../utils/journals/insights';
import { useStoryblok } from '../../utils/storyblok';
import {
  mapStoriesToPreviewItems,
  PreviewItem,
} from '../../utils/storyblok/stories';
import { pageTranslations } from '../../utils/translations';

type PageProps = {
  artists: AugmentedArtistCard[];
  glossary: GlossaryLink[];
  index: GlossaryIndex | null;
  insights: InsightItem[];
  locale: string;
  locales: string[];
  page: GlossaryTerm;
  preview: boolean;
  relatedTerms: GlossaryLink[];
  results: PreviewItem[];
  ogImageUrl: string;
};

export async function getStaticProps({
  locale = 'en',
  locales = ['en'],
  params,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<PageProps>> {
  const glossary = await fetchGlossary({ locale, preview });

  const page = glossary.find((item) => item.slug === params?.slug);
  if (!page) return { notFound: true };

  const [stories, index, results, translations] = await Promise.all([
    fetchByGlossaryTerm(page.uuid, { locale, preview }),
    fetchGlossaryIndex({ locale, preview }),
    fetchGlossaryResults(page.content.term, { locale, preview }),
    pageTranslations(locale, ['artists', 'glossary', 'og']),
  ]);

  const ogImageUrl = await imageOgStaticLoader({
    overlineKey: { key: 'og:glossary.overline' },
    titleKey: {
      key: 'og:glossary.term.title',
      options: {
        term: page.content.term,
      },
    },
    locale,
  });

  return {
    props: {
      artists: stories.artistsData,
      glossary: mapGlossaryLinks(
        glossary.filter((item) => item.slug !== page.slug),
      ),
      index,
      insights: stories.insightsData,
      locale,
      locales,
      page,
      preview,
      ogImageUrl,
      relatedTerms: mapGlossaryLinks(
        glossary.filter((item) =>
          (page.content.related || []).includes(item.uuid),
        ),
      ),
      results: mapStoriesToPreviewItems(results, page.content.term),
      ...translations,
    },
  };
}

const Page = ({
  artists,
  glossary,
  index,
  insights,
  locale,
  locales,
  ogImageUrl,
  page,
  preview,
  relatedTerms,
  results,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const story = useStoryblok(page, preview, locale);
  const { seo } = story.content;

  return (
    <Layout
      description={seo.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={seo.title}
    >
      <GlossaryTermPage
        artists={artists}
        glossary={glossary}
        index={index}
        insights={insights}
        page={story}
        relatedTerms={relatedTerms}
        results={results}
      />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
});

export default Page;
